// Templates 14–16 · Account · auth
// Platform-level.

import {
  renderShell,
  renderStatusDot,
  renderHeading,
  renderParagraph,
  renderInfoCard,
  renderButtonRow,
  renderFooter,
  tokens,
  cleanSubject,
} from './kit.ts';
import { escapeHtml } from './format.ts';
import type { RenderedEmail, MagicLinkData, WelcomeData, VendorTeamInviteData, VendorWelcomeData } from './types.ts';

const fontStack = `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
const monoStack = `ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, monospace`;
const displayStack = `'Bricolage Grotesque', 'DM Sans', sans-serif`;

// ─── 14 · MAGIC-LINK SIGN-IN ──────────────────────────────────────────────
// Note: typically Supabase Auth sends this, configured in dashboard with
// these markup placeholders. We expose the renderer here so the same
// chrome can be applied via custom SMTP if desired.
export function renderMagicLink(d: MagicLinkData): RenderedEmail {
  const subject = cleanSubject(`Your Goaround sign-in link`);
  const preheader = `Valid for ${d.ttlMinutes} minutes. Single use.`;

  const linkBlock = `<div style="background:${tokens.ink};border-radius:14px;padding:24px;margin:0 0 24px;text-align:center;">
    <div style="font-family:${monoStack};font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:${tokens.amber};margin-bottom:10px;">Magic link · ${d.ttlMinutes} min</div>
    <a href="${escapeHtml(d.signInUrl)}" style="display:inline-block;color:#ffffff;font-family:${displayStack};font-weight:700;font-size:20px;text-decoration:none;letter-spacing:-0.01em;">Sign in to Goaround →</a>
  </div>`;

  const codeBlock = `<div style="font-family:${fontStack};font-size:14px;color:${tokens.ink};margin:0 0 24px;">
    Or copy this 6-digit code into the sign-in screen:
    <span style="display:inline-block;background:${tokens.cardAmber};border-radius:6px;padding:6px 12px;margin-left:8px;font-family:${monoStack};font-weight:600;letter-spacing:0.14em;">${escapeHtml(d.code)}</span>
  </div>`;

  const body = `
    ${renderStatusDot('One-tap', 'action')}
    ${renderHeading('Tap to', 'come on in.')}
    ${renderParagraph(
      `Link below is good for ${d.ttlMinutes} minutes and one device. If you didn't request this, ignore it — nothing will happen.`,
    )}
    ${linkBlock}
    ${codeBlock}
  `;

  const footer = renderFooter({ tagline: `Powered by Supabase Auth`, pill: 'Auth' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Sign-in', body, footer }),
  };
}

// ─── 15 · WELCOME ─────────────────────────────────────────────────────────
export function renderWelcome(d: WelcomeData): RenderedEmail {
  const subject = cleanSubject(`Welcome to Goaround 👋`);
  const preheader = `Here's the 60-second tour, and a no-pressure first round.`;

  const stepHtml = (n: string, title: string, body: string) => `<table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;margin:0 0 14px;"><tr>
    <td valign="top" width="48" style="padding-right:14px;">
      <div style="font-family:${displayStack};font-weight:800;font-size:22px;color:${tokens.amber};line-height:1;">${escapeHtml(n)}</div>
    </td>
    <td valign="top">
      <div style="font-family:${fontStack};font-weight:600;font-size:16px;color:${tokens.ink};margin-bottom:2px;">${escapeHtml(title)}</div>
      <div style="font-family:${fontStack};font-size:14px;color:${tokens.inkSoft};line-height:1.5;">${escapeHtml(body)}</div>
    </td>
  </tr></table>`;

  const body = `
    ${renderStatusDot('Welcome', 'confirmed')}
    ${renderHeading("Glad you're", 'here.')}
    ${renderParagraph(
      `Goaround is pre-paid team happy hours at real NYC bars. You pick a package, we charge your card at 3 PM the day of, the bar pours when you walk in. That's it.`,
    )}
    <div style="margin:18px 0 24px;">
      ${stepHtml('01', 'Find a package', 'Filter by neighborhood, vibe, headcount.')}
      ${stepHtml('02', 'Invite the team', 'We send RSVPs and chase the maybes.')}
      ${stepHtml('03', 'Walk in cheers-ing', "Say your company name. The tab's covered.")}
    </div>
    ${renderButtonRow([
      { label: 'Plan your first round →', href: d.planUrl, style: 'pop' },
      { label: 'FAQ', href: d.faqUrl, style: 'ghost' },
    ])}
    ${renderParagraph(
      `— Maya, co-founder. Just hit reply if you have a question. It comes to me.`,
      { muted: true },
    )}
  `;

  const footer = renderFooter({ tagline: `One welcome per lifetime`, pill: 'Onboarding' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Day one', body, footer }),
  };
}

// ─── 17 · VENDOR WELCOME ──────────────────────────────────────────────────
// Sent at /onboarding step 1 the instant the vendor's auth account is created.
// Pairs with the magic-link email Supabase sends from `signInWithOtp`.
export function renderVendorWelcome(d: VendorWelcomeData): RenderedEmail {
  const subject = cleanSubject(
    d.vendorName
      ? `Welcome to Goaround — ${d.vendorName} is halfway listed.`
      : `Welcome to Goaround — your spot's halfway listed.`,
  );
  const preheader = `Your sign-in link is in a separate email. Finish your offer in a few minutes.`;

  const stepHtml = (n: string, title: string, body: string) => `<table cellpadding="0" cellspacing="0" border="0" width="100%" role="presentation" style="border-collapse:collapse;margin:0 0 14px;"><tr>
    <td valign="top" width="48" style="padding-right:14px;">
      <div style="font-family:${displayStack};font-weight:800;font-size:22px;color:${tokens.amber};line-height:1;">${escapeHtml(n)}</div>
    </td>
    <td valign="top">
      <div style="font-family:${fontStack};font-weight:600;font-size:16px;color:${tokens.ink};margin-bottom:2px;">${escapeHtml(title)}</div>
      <div style="font-family:${fontStack};font-size:14px;color:${tokens.inkSoft};line-height:1.5;">${escapeHtml(body)}</div>
    </td>
  </tr></table>`;

  const greeting = d.firstName
    ? `Hi ${escapeHtml(d.firstName)} — glad you're listing${d.vendorName ? ` <strong>${escapeHtml(d.vendorName)}</strong>` : ''}.`
    : `Glad you're listing${d.vendorName ? ` <strong>${escapeHtml(d.vendorName)}</strong>` : ' your spot'}.`;

  const body = `
    ${renderStatusDot('Vendor account · created', 'confirmed')}
    ${renderHeading("You're", 'in.', '')}
    ${renderParagraph(greeting)}
    ${renderParagraph(
      `We just created your Goaround vendor account. A separate email has a one-tap magic link to sign in — no password to set, no extra step. Your offer isn't live yet; finish step two whenever, then we'll review it (usually same day).`,
    )}
    <div style="margin:18px 0 24px;">
      ${stepHtml('01', 'Finish your offer', 'Pick package tiers, set what teams pay, add photos.')}
      ${stepHtml('02', 'We review', "Quick check that the offer looks right. Most spots hear back the same day.")}
      ${stepHtml('03', 'Bookings start landing', 'Teams book direct. Goaround settles the tab after each event runs.')}
    </div>
    ${renderButtonRow([
      { label: 'Pick up where you left off →', href: d.continueUrl, style: 'pop' },
      { label: 'How it works', href: d.faqUrl, style: 'ghost' },
    ])}
    ${renderParagraph(
      `— Maya, co-founder. Reply to this email if anything is broken or unclear. It comes straight to me.`,
      { muted: true },
    )}
  `;

  const footer = renderFooter({ tagline: `One welcome per vendor account`, pill: 'Onboarding' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Vendor day one', body, footer }),
  };
}

// ─── 16 · VENDOR TEAM-MEMBER INVITE ───────────────────────────────────────
export function renderVendorTeamInvite(d: VendorTeamInviteData): RenderedEmail {
  const subject = cleanSubject(`${d.inviterFullName} added you to ${d.vendorName} on Goaround`);
  const preheader = `${d.role} access · accept to start managing bookings.`;

  const inviterFirst = d.inviterFullName.split(' ')[0];

  const body = `
    ${renderStatusDot('Team invite', 'action')}
    ${renderHeading(`${inviterFirst} wants you on the`, d.vendorName, 'team.')}
    ${renderParagraph(
      `She added you as a ${escapeHtml(d.role)}. Once you accept, you'll see incoming bookings, run-sheets, and weekly performance for ${escapeHtml(d.vendorName.split(' ')[0])} — same view ${escapeHtml(inviterFirst)} has.`,
    )}
    ${renderInfoCard(
      [
        { label: 'Bar', value: `${escapeHtml(d.vendorName)}${d.vendorNeighborhood ? ` · ${escapeHtml(d.vendorNeighborhood)}` : ''}` },
        { label: 'Invited by', value: `${escapeHtml(d.inviterFullName)} · ${escapeHtml(d.inviterEmail)}` },
        { label: 'Role', value: `${escapeHtml(d.role)} (read &amp; edit bookings)` },
      ],
      { tone: 'amber' },
    )}
    ${renderButtonRow([
      { label: 'Accept invite →', href: d.acceptUrl, style: 'primary' },
      { label: 'Decline', href: d.declineUrl, style: 'ghost' },
    ])}
    ${renderParagraph(`Not expecting this? You can decline; ${escapeHtml(inviterFirst)} will be notified.`, { muted: true })}
  `;

  const footer = renderFooter({ tagline: `Invites expire in 7 days`, pill: 'Team' });

  return {
    subject,
    preheader,
    html: renderShell({ preheader, headerLabel: 'Team invite', body, footer }),
  };
}
